import { useState, useRef, useEffect, useCallback } from 'react';

interface UseDocumentUrlRefreshOptions {
  /**
   * Callback to fetch a fresh presigned URL for a document.
   * Should return the fresh URL or null if not available.
   */
  onRefreshUrl?: (documentId: string) => Promise<string | null>;
  /**
   * Initial document URLs mapping (documentId -> url)
   */
  initialUrls?: Record<string, string>;
  /**
   * Debounce delay in milliseconds (default: 300ms)
   */
  debounceMs?: number;
}

interface DocumentState {
  isPersisted?: boolean;
}

/**
 * Custom hook to manage document URL refreshing with debouncing and request cancellation.
 *
 * This hook handles:
 * - Debounced URL refresh when switching between documents
 * - Request cancellation when user quickly flips through documents
 * - Immediate URL refresh for "open in new tab" actions
 *
 * NOTE: The refresh mechanism currently fetches ALL document URLs from the API.
 * This is a quick 80/20 fix. A more optimized approach would be to add a dedicated API
 * endpoint that returns only a single document's presigned URL.
 */
export function useDocumentUrlRefresh({
  onRefreshUrl,
  initialUrls = {},
  debounceMs = 300,
}: UseDocumentUrlRefreshOptions) {
  const [localDocumentUrls, setLocalDocumentUrls] =
    useState<Record<string, string>>(initialUrls);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // For debouncing/cancellation when user quickly flips through documents
  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);

  // Update local URLs when initial URLs change
  useEffect(() => {
    setLocalDocumentUrls(prev => ({ ...prev, ...initialUrls }));
  }, [initialUrls]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current);
      }
      if (refreshAbortControllerRef.current) {
        refreshAbortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Refresh URL for a document to ensure presigned URL is valid.
   * Can be called directly for immediate refresh (e.g., open in new tab)
   * or through handleDebouncedRefresh for debounced refresh.
   *
   * Note: This does NOT update local state directly. The URL update flows through:
   * onRefreshUrl -> parent state (documentUrls) -> initialUrls prop -> effect -> localDocumentUrls
   * This prevents double-loading when the URL changes.
   */
  const refreshUrlForDocument = useCallback(
    async (
      documentId: string,
      documentState?: DocumentState,
      signal?: AbortSignal
    ): Promise<string | null> => {
      // Skip refresh for local documents (not persisted, using blob URLs)
      if (documentState && !documentState.isPersisted) {
        return localDocumentUrls[documentId] || null;
      }

      if (!onRefreshUrl) {
        // If no refresh callback provided, use existing URL
        return localDocumentUrls[documentId] || null;
      }

      setIsRefreshing(true);
      try {
        const freshUrl = await onRefreshUrl(documentId);
        // Check if request was aborted before returning
        if (signal?.aborted) {
          return null;
        }
        // Return fresh URL - state update flows through parent via initialUrls
        return freshUrl || localDocumentUrls[documentId] || null;
      } catch (err) {
        // Don't log abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return null;
        }
        console.error('Error refreshing document URL:', err);
        return localDocumentUrls[documentId] || null;
      } finally {
        if (!signal?.aborted) {
          setIsRefreshing(false);
        }
      }
    },
    [onRefreshUrl, localDocumentUrls]
  );

  /**
   * Handle document change with debounced URL refresh.
   * Immediately calls onActiveDocumentChange for responsive UI,
   * then debounces the URL refresh to prevent excessive API calls.
   */
  const handleDebouncedRefresh = useCallback(
    (documentId: string, documentState?: DocumentState) => {
      // Cancel any pending refresh request
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current);
      }
      if (refreshAbortControllerRef.current) {
        refreshAbortControllerRef.current.abort();
      }

      // Debounce the URL refresh
      refreshDebounceRef.current = setTimeout(async () => {
        const abortController = new AbortController();
        refreshAbortControllerRef.current = abortController;
        await refreshUrlForDocument(
          documentId,
          documentState,
          abortController.signal
        );
      }, debounceMs);
    },
    [refreshUrlForDocument, debounceMs]
  );

  /**
   * Get a fresh URL for opening in new tab (immediate, no debounce)
   */
  const getUrlForNewTab = useCallback(
    async (
      documentId: string,
      documentState?: DocumentState
    ): Promise<string | null> => {
      return refreshUrlForDocument(documentId, documentState);
    },
    [refreshUrlForDocument]
  );

  /**
   * Update URLs directly (e.g., for locally added documents with blob URLs)
   */
  const updateUrls = useCallback((urls: Record<string, string>) => {
    setLocalDocumentUrls(prev => ({ ...prev, ...urls }));
  }, []);

  /**
   * Remove a URL from the local state.
   * Also used to clear stale URLs when switching documents to trigger loading state.
   */
  const removeUrl = useCallback((documentId: string) => {
    setLocalDocumentUrls(prev => {
      const updated = { ...prev };
      delete updated[documentId];
      return updated;
    });
  }, []);

  return {
    documentUrls: localDocumentUrls,
    isRefreshing,
    handleDebouncedRefresh,
    getUrlForNewTab,
    updateUrls,
    removeUrl,
  };
}
